import { useState, type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";

import {
  classifyFlowWriteFailure,
  useFlowSettingsQuery,
  useReplaceFlowSettingsMutation,
  type FlowWriteFailure,
  type ProjectFlowSettings,
} from "@/api/flowSettings";
import { queryKeys } from "@/api/hooks";
import type { LibraryAsset } from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import {
  maxSlotCount,
  priorityWrites,
  resizeSlots,
  slotDraftOf,
  slotEditBase,
  slotWrites,
  servedLimit,
  stagedKey,
  stagedWrites,
  toggleAlways,
  type SlotDraft,
  type StagedChange,
} from "@/lib/flowSettings";
import {
  legacyRemovals,
  moveRequired as moveRequiredSlots,
  releaseRequired,
} from "@/lib/interviewTracks";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";
import type { SectionReviewPanelContext } from "@/ui/review/SectionReviewList";

import { InterviewTracksPanel } from "./InterviewTracks";

/**
 * The Project Pathways flow surface (#855), over #854's typed contract.
 *
 * ## WHAT THE SWITCH ACTUALLY WRITES
 *
 * There is no Always/Dynamic mode field on the wire, on purpose: selection is
 * DERIVED from `dynamic_track_slots`. So a track's Required switch edits the
 * SLOT LIST: on takes the first automatic slot and pins the track, off returns
 * that slot to `auto`, which is what keeps the dynamic limit fixed while the
 * selection moves. For the MOD/NII source recipe that is `[ptsd, auto, auto]`:
 * three slots, one held by a required track, two left for classification.
 *
 * ## THE LIMIT IS THE SLOT COUNT, AND NOTHING HERE IS A CONSTANT
 *
 * `maxDynamicTracks` is the obsolete spelling of the limit; in the slots shape
 * the loader derives it from the slot list. So Tracks per interview grows and
 * shrinks the list, and the upper bound offered comes from the response's own
 * `fillableTrackCount`: the pinned loader's rule, not a product rule and not a
 * literal three.
 */

export type PathwayFlowSurface = {
  readonly projectId: string;
  readonly query: UseQueryResult<ProjectFlowSettings>;
  /**
   * The reading this surface renders, or `null` when there is none yet.
   *
   * May be a RETAINED reading whose latest refetch failed — see `settled`,
   * which is what write controls are gated on.
   */
  readonly state: ProjectFlowSettings | null;
  /**
   * What the reading on screen currently IS.
   *
   * Three states, because TanStack reports two of them the same way. It keeps
   * `data` while a refetch runs AND after one fails, and `status` stays
   * `success` throughout the first — so `isSuccess` alone answers "has this
   * query ever succeeded", not "is what I am showing current", which is the
   * question a write has to ask. Measured against the installed
   * `@tanstack/query-core` 5.101.4, not assumed.
   *
   *   `current`     the reading is the service's latest answer
   *   `refreshing`  a re-read is in flight, so this reading is already
   *                 superseded — the window an adoption opens, since an
   *                 adoption invalidates this key
   *   `paused`      a re-read was started and is PARKED (offline, or a held
   *                 retry). Superseded like `refreshing`, but it does not
   *                 resolve on its own and a bare `refetch()` returns the
   *                 parked retryer's promise without sending anything, so this
   *                 state carries the cancel-then-refetch recovery instead
   *   `behind`      the re-read failed; the reading is retained
   *
   * FOUR, not three, because `fetchStatus` distinguishes fetching from paused
   * and `status` reports neither: a parked refetch is `isSuccess: true,
   * isFetching: false`, which round 3 classified as `current` and offered
   * writes against indefinitely. Derived from `fetchStatus` first for that
   * reason.
   *
   * The reading is SHOWN in all four — blanking a section over a transient
   * outage is worse — but a write is offered only in the first, and the others
   * say which they are. `refetchOnWindowFocus` is off for this client, so
   * `refreshing` is not a background flicker: it means something invalidated
   * this read.
   */
  readonly readState: "current" | "refreshing" | "paused" | "behind";
  /** `readState === "current"`. The one gate every write control reads. */
  readonly settled: boolean;
  /**
   * Re-read this Project's flow settings.
   *
   * `cancelQueries` then `refetchQueries`, never a bare `refetch()`: a PAUSED
   * query answers `refetch()` with the parked retryer's promise without firing
   * anything — which is exactly the state this recovery is offered in.
   */
  readonly retryRead: () => void;
  readonly slots: readonly SlotDraft[];
  /**
   * The dynamic limit to DISPLAY: the pending slot count while editing, and
   * otherwise the service's own `maxDynamicTracks` for whichever shape the
   * Project is in. Null when the service could not resolve one.
   */
  readonly displayLimit: number | null;
  /** Whether the served document is still in the replaced selection shape. */
  readonly legacyShape: boolean;
  /**
   * A stale refusal stands, and no further save may be attempted until the
   * author discards or reloads. See `submit`.
   */
  readonly staleBlocked: boolean;
  /**
   * Why a save cannot be made right now, or `null` when one can.
   *
   * ONE RULE, read by every control that could start a write and re-checked
   * inside `submit` itself. The confirmation dialog is the reason it exists:
   * it is opened from a state that permits a save and can outlive it — a
   * refetch fails, or a concurrent write lands — and a dialog carrying its own
   * `disabled` expression would have gone on submitting after the bar behind it
   * had stopped.
   */
  readonly saveRefusal: string | null;
  readonly priorities: ReadonlyMap<string, string>;
  /** Removals and restores staged for the next save, by their address. */
  readonly staged: ReadonlyMap<string, StagedChange>;
  readonly dirty: boolean;
  readonly canWrite: boolean;
  readonly writeReason: string | null;
  readonly saving: boolean;
  /** A change this surface refused before a round trip, with the remedy. */
  readonly localRefusal: string | null;
  readonly priorityRefusals: ReadonlyMap<string, string>;
  readonly failure: FlowWriteFailure | null;
  readonly savedAt: number | null;
  /** Whether the Save confirmation is open. */
  readonly confirming: boolean;
  readonly toggleAlways: (track: string, on: boolean) => void;
  readonly resize: (count: number) => void;
  readonly moveRequired: (track: string, to: number) => void;
  readonly editPriority: (track: string, text: string) => void;
  readonly stage: (change: StagedChange) => void;
  readonly askToSave: () => void;
  readonly cancelSave: () => void;
  readonly discard: () => void;
  readonly save: () => void;
};

/**
 * The flow read, the pending edits and the one write, held once.
 *
 * HELD BY `SectionDiagnostics` and passed down, on the same one-read discipline
 * the rest of that screen follows: the panel and the notices above
 * them both describe this state, and two cache observers of one key are two
 * things that can disagree in front of a clinician.
 *
 * `enabled` is false on the five sections that have no flow control, so they
 * issue no request at all — a hook may not be called conditionally, and a
 * disabled query is the supported way to say "not this section".
 *
 * ## ONE REQUEST, BECAUSE THE LOADER JUDGES A DOCUMENT
 *
 * Every pending change — the slot list, each track's priority, each staged
 * removal or restore — goes in ONE `PUT`. That is not batching for its own
 * sake: the service validates the DOCUMENT this write would leave, and a
 * default Project sits in the loader's legacy selection shape, where authoring
 * slots without removing `default_tracks` and `max_dynamic_tracks` declares two
 * shapes at once and removing either without the slots declares neither. Both
 * are refused. Writing the three together is the only path there is, and the
 * service accepts it in one revisioned commit.
 */
export function usePathwayFlow(
  projectId: string,
  { enabled }: { enabled: boolean },
): PathwayFlowSurface {
  const query = useFlowSettingsQuery(projectId, { enabled });
  const mutation = useReplaceFlowSettingsMutation(projectId);
  const write = useCapability("draft.write");
  const retryRead = useRetryPausedQuery(queryKeys.projectFlow(projectId));

  // NULL UNTIL THE FIRST EDIT, so a refetch that brings a newer document
  // through is adopted by a surface nobody is editing, and is NOT adopted over
  // entered text. Once the author has touched the form, what they typed stays
  // on screen through every refetch and through every refusal — the ticket's
  // "cancellation, stale save and invalid content preserve entered text".
  const [slotDraft, setSlotDraft] = useState<SlotDraft[] | null>(null);
  const [priorityDraft, setPriorityDraft] = useState<Map<string, string>>(
    () => new Map(),
  );
  const [staged, setStaged] = useState<Map<string, StagedChange>>(
    () => new Map(),
  );
  const [localRefusal, setLocalRefusal] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // THE REVISION THIS EDIT IS BOUND TO, captured when editing began.
  //
  // Round 1 sent `state.draftVersion` at submit time. A stale refusal refetches
  // the flow read, so the very next Save carried the edits the author had kept
  // — correctly — but bound to the version that refusal had just fetched:
  // the compare-and-set then PASSED and silently overwrote the concurrent
  // change nobody had looked at. The whole point of the check is that a write
  // built on a revision the author never saw is refused, so the version is
  // captured once and never re-read, exactly as `ResponseFilteringControl`
  // captures its CAS token.
  const [editVersion, setEditVersion] = useState<string | null>(null);
  //: A stale refusal is TERMINAL for this edit. Retrying cannot be safe — the
  //: document moved under it — so the only ways on are Discard (drop the edit)
  //: or a reload (see it).
  const [staleBlocked, setStaleBlocked] = useState(false);

  const state = query.data ?? null;
  // See `PathwayFlowSurface.readState`. `fetchStatus` FIRST, because it is the
  // only field that separates a re-read in flight from one that is parked;
  // `status` reports a parked refetch as `success` and `isFetching` as false,
  // which is how round 3's derivation called it `current`.
  const readState: "current" | "refreshing" | "paused" | "behind" =
    query.fetchStatus === "fetching"
      ? "refreshing"
      : query.fetchStatus === "paused"
        ? "paused"
        : query.isSuccess
          ? "current"
          : "behind";
  const settled = readState === "current";
  const served = state === null ? [] : slotDraftOf(state);
  const slots = slotDraft ?? served;
  // What an EDIT starts from, which on the legacy shape is the limit the
  // Project already runs written as automatic slots. Never `slots` itself: that
  // would report a Project nobody has touched as dirty.
  const editBase = slotDraft ?? (state === null ? [] : slotEditBase(state));

  const priorityEntries = state?.trackPriorityEntries ?? [];
  const { writes: priorityChanges, refusals: priorityRefusals } = priorityWrites(
    priorityEntries,
    priorityDraft,
  );
  const slotChanges =
    state === null || staged.has(stagedKey("dynamicTrackSlots", null))
      ? []
      : slotWrites(state, slots);
  const dirty =
    staged.size > 0 ||
    slotChanges.length > 0 ||
    priorityChanges.length > 0 ||
    // A refused priority is dirty too: the field holds text that differs from
    // the served value, and calling it clean would let the author navigate away
    // believing the number they typed had been kept.
    priorityRefusals.size > 0;

  function beginEdit() {
    setSavedAt(null);
    if (state !== null) {
      setEditVersion((current) => current ?? state.draftVersion);
    }
  }

  function changeSlots(next: { slots: SlotDraft[]; refusal: string | null }) {
    setLocalRefusal(next.refusal);
    if (next.refusal !== null) {
      return;
    }
    setSlotDraft(next.slots);
    beginEdit();
    // The request refuses two ops at one address, so an edit drops the staged one.
    setStaged((current) =>
      withLegacyRemovals(
        without(
          without(
            without(current, stagedKey("dynamicTrackSlots", null)),
            stagedKey("defaultTracks", null),
          ),
          stagedKey("maxDynamicTracks", null),
        ),
        next.slots,
      ),
    );
  }

  function withLegacyRemovals(
    current: Map<string, StagedChange>,
    slots: readonly SlotDraft[],
  ) {
    if (state === null || slots.length === 0) {
      return current;
    }
    const next = new Map(current);
    for (const change of legacyRemovals(state)) {
      next.set(stagedKey(change.setting, change.track), change);
    }
    return next;
  }

  function clear() {
    setSlotDraft(null);
    setPriorityDraft(new Map());
    setStaged(new Map());
    setLocalRefusal(null);
    setConfirming(false);
    setEditVersion(null);
    setStaleBlocked(false);
  }

  const displayedLimit =
    slotDraft !== null
      ? slotDraft.length
      : state === null
        ? null
        : servedLimit(state);

  const saveRefusal = !write.allowed
    ? (write.reason ??
      "This account may not change this project's dynamic phase settings.")
    : readState === "refreshing"
      ? "This project's dynamic phase is being re-read. Saving waits for that to land, so a change is never written against a reading the Editor Service has already replaced."
      : readState === "paused"
        ? "A re-read of this project's dynamic phase is waiting for the connection. Until it completes, the selection here is the previous reading and nothing can be saved against it."
        : readState === "behind"
          ? "This project's dynamic phase could not be re-read, so nothing here can be saved against it. Read it again first."
          : staleBlocked
          ? "This project's draft changed since this screen read it. Saving stays refused until you reload the section or discard."
          : priorityRefusals.size > 0
            ? "One of the fallback orders is not a whole number. Correct it, or restore it to the base pack's value."
            : null;

  return {
    projectId,
    query,
    state,
    readState,
    settled,
    retryRead,
    saveRefusal,
    slots,
    displayLimit: displayedLimit,
    legacyShape: state?.selection.shape === "legacy",
    staleBlocked,
    priorities: priorityDraft,
    staged,
    dirty,
    canWrite: write.allowed,
    writeReason: write.reason,
    saving: mutation.isPending,
    localRefusal,
    priorityRefusals,
    failure: mutation.isError ? classifyFlowWriteFailure(mutation.error) : null,
    savedAt,
    confirming,
    toggleAlways(track, on) {
      changeSlots(
        on
          ? toggleAlways(editBase, track, true)
          : { slots: releaseRequired(editBase, track), refusal: null },
      );
    },
    moveRequired(track, to) {
      changeSlots({ slots: moveRequiredSlots(editBase, track, to), refusal: null });
    },
    resize(count) {
      // The loader's own upper bound, from the response, applied here rather
      // than left to the stepper's own disabled state.
      changeSlots(
        resizeSlots(editBase, count, state === null ? null : maxSlotCount(state)),
      );
    },
    editPriority(track, text) {
      setLocalRefusal(null);
      beginEdit();
      setPriorityDraft((current) => new Map(current).set(track, text));
      // A legacy project is converted to slots whatever the edit, so its removals are staged for the confirmation.
      if (state?.selection.shape === "legacy" && editBase.length > 0) {
        setSlotDraft(editBase);
      }
      setStaged((current) =>
        withLegacyRemovals(
          without(current, stagedKey("trackPriority", track)),
          editBase,
        ),
      );
    },
    stage(change) {
      const key = stagedKey(change.setting, change.track);
      setLocalRefusal(null);
      beginEdit();
      setStaged((current) => {
        const next = new Map(current);
        // A SECOND PRESS UNDOES IT. The control is the only place this decision
        // exists before a save, so it has to be reversible without a Discard
        // that would take every other pending edit with it.
        if (next.get(key)?.op === change.op) {
          next.delete(key);
        } else {
          next.set(key, change);
        }
        // Restoring a replaced key supersedes the removal a slot edit staged for the other one.
        if (
          change.op === "reset" &&
          (change.setting === "defaultTracks" ||
            change.setting === "maxDynamicTracks")
        ) {
          for (const other of ["defaultTracks", "maxDynamicTracks"] as const) {
            const otherKey = stagedKey(other, null);
            if (otherKey !== key && next.get(otherKey)?.op === "delete") {
              next.delete(otherKey);
            }
          }
        }
        return next;
      });
      if (
        change.setting === "dynamicTrackSlots" ||
        change.setting === "defaultTracks" ||
        change.setting === "maxDynamicTracks"
      ) {
        setSlotDraft(null);
      }
      const track = change.track;
      if (change.setting === "trackPriority" && track !== null) {
        setPriorityDraft((current) => without(current, track));
      }
    },
    askToSave() {
      if (state === null || saveRefusal !== null) {
        return;
      }
      // CONFIRMED ONLY WHERE SOMETHING IS DISCARDED. A slot or priority edit is
      // an ordinary revisioned write and needs no ceremony; a removal takes a
      // key out of the document the pinned engine loads and a restore drops
      // this Project's own value, and both are named before they happen.
      if (staged.size === 0) {
        void submit();
        return;
      }
      setConfirming(true);
    },
    cancelSave() {
      // EVERY PENDING CHANGE SURVIVES. Cancelling the confirmation is not
      // Discard: nothing is written and nothing entered is lost, which is the
      // ticket's own criterion.
      setConfirming(false);
    },
    discard() {
      clear();
      setSavedAt(null);
      mutation.reset();
    },
    save: submit,
  };

  function submit() {
    if (state === null || saveRefusal !== null) {
      return;
    }
    const settings = [
      ...stagedWrites(staged),
      ...slotChanges,
      ...priorityChanges,
    ];
    if (settings.length === 0) {
      return;
    }
    mutation.mutate(
      // THE CAPTURED REVISION, not whatever the last refetch left behind.
      { expectedDraftVersion: editVersion ?? state.draftVersion, settings },
      {
        onSuccess() {
          // The RESPONSE is now the served state, so the local draft is
          // dropped and the surface reads back what the server derived — which
          // is the effective-state readback this ticket asks for, not an echo
          // of what was typed.
          clear();
          setSavedAt(Date.now());
        },
        onError(error) {
          // The confirmation closes; the pending changes do not. A refusal is
          // something to read and act on, not a reason to lose the work.
          setConfirming(false);
          // A STALE REFUSAL ENDS THIS EDIT. Everything entered is kept so the
          // author can copy it forward, and Save stays refused until they
          // discard or reload — because the only thing a second press could do
          // is rebase their edit onto a document they have not seen.
          if (classifyFlowWriteFailure(error).kind === "stale") {
            setStaleBlocked(true);
          }
        },
      },
    );
  }
}

function without<T>(map: ReadonlyMap<string, T>, key: string): Map<string, T> {
  const next = new Map(map);
  next.delete(key);
  return next;
}

/**
 * The `controls`-slot content, or `undefined` when there is nothing to put
 * there.
 *
 * A FUNCTION rather than a component, and that is the whole reason it exists in
 * this shape: `ProjectSectionPane` renders a `space-y-3` wrapper for whatever it
 * is handed, so a component that renders `null` still opens a blank gap above
 * the rows — the defect #909 round 2 fixed, and the reason that pane's own note
 * says the caller must ask first. The Interview tracks panel
 * arrives through the section review's `panel`, so on a clean, settled
 * Project this slot legitimately holds nothing at all.
 *
 * What it does hold is what an author must read BEFORE the rows: a finding the
 * pinned loader would refuse, a change this surface refused, a write the service
 * refused, and the pending-change bar. Above the rows because `ProjectSectionPane`
 * puts what configures the rows ahead of them, and because a refusal an author
 * has to scroll past the list to find is a hidden refusal.
 */
export function pathwayFlowControls(
  surface: PathwayFlowSurface,
): ReactNode | undefined {
  const notices = <PathwayFlowNotices surface={surface} />;
  const hasSomethingToSay =
    surface.state === null ||
    surface.readState !== "current" ||
    surface.state.findings.length > 0 ||
    surface.localRefusal !== null ||
    surface.failure !== null ||
    !surface.canWrite ||
    surface.dirty ||
    surface.savedAt !== null ||
    surface.confirming;
  return hasSomethingToSay ? notices : undefined;
}

function PathwayFlowNotices({ surface }: { surface: PathwayFlowSurface }) {
  const { query, state } = surface;

  if (state === null) {
    // NO READING AT ALL — the FIRST read has not succeeded. Round 4 gave the
    // retained-reading states a recovery and left this one with none, so a
    // first read that failed or parked was a dead end until the route was left
    // and re-entered. `isPending` is the only arm with nothing to offer: it is
    // still in flight.
    const reading = query.isPending && query.fetchStatus === "fetching";
    return (
      <div
        data-slot="pathway-flow"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-card px-5 py-4 text-sm text-muted-foreground"
      >
        <p className="min-w-0 flex-1" role={reading ? "status" : "alert"}>
          {reading
            ? "Reading how this project routes its dynamic phase…"
            : query.fetchStatus === "paused"
              ? "Reading this project's dynamic phase is waiting for the connection. The pathways below are listed without their runtime selection; adoption is unaffected."
              : "This project's dynamic phase settings could not be read, so the pathways below are listed without their runtime selection. Adoption is unaffected."}
        </p>
        {reading ? null : (
          <Button size="sm" variant="outline" onClick={surface.retryRead}>
            Read it again
          </Button>
        )}
      </div>
    );
  }

  return (
    <div data-slot="pathway-flow" className="space-y-3">
      {surface.readState === "refreshing" ? (
        // A RE-READ IS IN FLIGHT, so what is below is already superseded. This
        // is the window an adoption opens — it invalidates this key — and
        // `isSuccess` stays true right through it, which is why gating on that
        // alone left every control writable here. Announced politely rather
        // than as an alert: nothing is wrong, and it resolves on its own.
        <p role="status" aria-live="polite" className="text-xs text-muted-foreground">
          Re-reading this project&apos;s dynamic phase. The selection below is
          the previous reading until that lands, so changes wait for it.
        </p>
      ) : surface.readState === "paused" ? (
        // PARKED, NOT FAILED, and it will not resolve on its own. The recovery
        // is `cancelQueries` then `refetchQueries`: a bare `refetch()` on a
        // paused query answers with the parked retryer's own promise without
        // sending anything, which is the button that does nothing
        // `useRetryPausedQuery` exists to avoid.
        <Notice tone="warning" role="alert">
          <span>
            A re-read of this project&apos;s dynamic phase is waiting for the
            connection, so the selection below is the previous reading. Nothing
            here can be changed until it completes.
          </span>
          <Button size="sm" variant="outline" onClick={surface.retryRead}>
            Read it again
          </Button>
        </Notice>
      ) : surface.readState === "behind" ? (
        // A failed refetch keeps the old data: say so and keep the rows, but withhold the write controls.
        <Notice tone="warning" role="alert">
          <span>
            This project&apos;s dynamic phase could not be re-read, so the
            selection below is the last reading that succeeded and may be behind
            what the Editor Service now holds. Nothing here can be changed until
            it can be read again.
          </span>
          <Button size="sm" variant="outline" onClick={surface.retryRead}>
            Read it again
          </Button>
        </Notice>
      ) : null}

      {state.findings.length > 0 ? (
        <Notice tone="destructive" role="alert">
          <span>
            The pinned engine would not load this project&apos;s dynamic phase
            as it stands. {state.findings[0]?.message} A freeze is refused until
            it is resolved.
            {state.findings.length > 1
              ? ` ${state.findings.length - 1} further ${
                  state.findings.length === 2 ? "reason is" : "reasons are"
                } reported under More about these settings.`
              : ""}
          </span>
        </Notice>
      ) : null}

      {surface.localRefusal !== null ? (
        <Notice tone="warning" role="alert">
          <span>{surface.localRefusal}</span>
        </Notice>
      ) : null}

      {surface.failure !== null ? (
        <FlowFailureNotice failure={surface.failure} />
      ) : null}

      {!surface.canWrite ? (
        <p className="text-xs text-muted-foreground">
          {surface.writeReason ??
            "This account may read this project's dynamic phase settings but not change them."}
        </p>
      ) : null}

      {surface.dirty ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-card px-4 py-3">
          <p className="min-w-0 flex-1 text-sm text-foreground">
            Unsaved changes to the dynamic phase. Nothing is written until you
            save, and everything below is saved together as one revision.
          </p>
          {surface.saveRefusal !== null ? (
            // A DISABLED SAVE MUST SAY WHY. The reason was rendered only inside
            // the confirmation, which a refused save never reaches — so the
            // button simply stopped working with nothing on screen accounting
            // for it.
            <p
              role="alert"
              className="basis-full text-xs text-muted-foreground"
            >
              {surface.saveRefusal}
            </p>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={surface.discard}
            disabled={surface.saving}
          >
            Discard
          </Button>
          <Button
            size="sm"
            onClick={surface.askToSave}
            disabled={surface.saving || surface.saveRefusal !== null}
          >
            {surface.saving ? "Saving…" : "Save"}
          </Button>
        </div>
      ) : surface.savedAt !== null ? (
        <p role="status" className="text-xs text-muted-foreground">
          Saved. The selection below is the Editor Service&apos;s own reading of
          the document this project now stores.
        </p>
      ) : null}

      {surface.confirming ? <SaveConfirmDialog surface={surface} /> : null}
    </div>
  );
}

/**
 * The classified write failure, in the caller's words for a state the caller
 * can act on and in the SERVER's for a refusal only it can explain.
 */
function FlowFailureNotice({ failure }: { failure: FlowWriteFailure }) {
  const text = ((): string => {
    switch (failure.kind) {
      case "stale":
        return "This project's draft changed since this screen read it, so nothing was written. What you entered is kept, and saving stays refused: applying it now would write it over a change nobody here has seen. Reload the section to see the current settings, then re-enter it — or discard.";
      case "conflict":
        return `${failure.message} Nothing was written and what you entered is kept.`;
      case "upstream":
        return "The Editor Service could not reach the pinned clinical engine, so it could not check this change against the loader. Nothing was written. This one is worth trying again.";
      case "denied":
        return "This account may not change this project's dynamic phase settings. Nothing was written.";
      case "invalid":
        // THE SERVER'S SENTENCE ALONE. `ErrorDetail.field` is a dotted request
        // address, and #488's amendment bans a dotted address on a Project
        // route; the message is already field-addressed in words, which is the
        // half an author can act on.
        return `${failure.message} Nothing was written and what you entered is kept.`;
      case "ambiguous":
        return "The Editor Service did not answer this write, so it is not known whether it was applied. Nothing here has been resent. Reload the section before trying again.";
      case "error":
        return `The Editor Service refused this write (${failure.code}). Nothing was written and what you entered is kept.`;
    }
  })();
  return (
    <Notice tone="destructive" role="alert">
      <span>{text}</span>
    </Notice>
  );
}

/**
 * The one confirmation, and it names what is discarded.
 *
 * Shown only when the pending set contains a removal or a restore. Cancel
 * writes nothing and keeps every pending change, which is what makes a
 * cancelled save cost the author nothing.
 */
function SaveConfirmDialog({ surface }: { surface: PathwayFlowSurface }) {
  const removals = [...surface.staged.values()].filter(
    (change) => change.op === "delete",
  );
  const restores = [...surface.staged.values()].filter(
    (change) => change.op === "reset",
  );
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !surface.saving) {
          surface.cancelSave();
        }
      }}
    >
      <DialogContent
        busy={surface.saving}
        title="Save the dynamic phase"
        description="One revision, checked against the pinned engine's own loader before anything is stored."
        footer={
          <>
            <Button
              variant="outline"
              onClick={surface.cancelSave}
              disabled={surface.saving}
            >
              Cancel
            </Button>
            <Button
              onClick={surface.save}
              disabled={surface.saving || surface.saveRefusal !== null}
            >
              {surface.saving ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          {removals.length > 0 ? (
            <div>
              <p className="font-medium text-foreground">
                Removed from the document the pinned engine loads
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
                {removals.map((change) => (
                  <li key={`${change.setting}:${change.track ?? ""}`}>
                    {change.subject}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                Removed is not set to nothing: the loader will not see the key
                at all.
              </p>
            </div>
          ) : null}

          {restores.length > 0 ? (
            <div>
              <p className="font-medium text-foreground">
                Restored to the base pack&apos;s value
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
                {restores.map((change) => (
                  <li key={`${change.setting}:${change.track ?? ""}`}>
                    {change.subject}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                This project&apos;s own value is dropped, and the base pack
                supplies the setting again.
              </p>
            </div>
          ) : null}

          {surface.saveRefusal !== null ? (
            // THE STATE CHANGED UNDER THIS DIALOG. It was opened from a savable
            // surface; a failed refetch or a concurrent write can end that
            // while it stands, and a Save that had merely gone quiet would look
            // like a slow request rather than a refusal.
            <Notice tone="warning" role="alert">
              <span>{surface.saveRefusal}</span>
            </Notice>
          ) : null}

          <p className="text-xs text-muted-foreground">
            Cancelling writes nothing and keeps every change on this screen.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function readable(track: string): string {
  const spaced = track.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The Interview tracks panel, or `undefined` until the flow read settles. */
export function pathwayFlowPanel({
  surface,
  catalog,
  projectId,
}: {
  surface: PathwayFlowSurface;
  /** The caller's catalog read, for clinical names. `null` where unread. */
  catalog: readonly LibraryAsset[] | null;
  projectId: string;
}): ((context: SectionReviewPanelContext) => ReactNode) | undefined {
  const state = surface.state;
  if (state === null) {
    return undefined;
  }

  const nameById = new Map(
    (catalog ?? []).map((asset) => [asset.id, asset.name] as const),
  );
  // TRACK → the clinical name of the Pathway adopted on it. Falls back to the
  // track's registered name, capitalised, where no adopted Pathway names it.
  const trackNames = new Map<string, string>();
  for (const pin of state.adoptedPathways) {
    const name = nameById.get(pin.assetId);
    if (name !== undefined && !trackNames.has(pin.track)) {
      trackNames.set(pin.track, name);
    }
  }
  const nameOfTrack = (track: string): string =>
    trackNames.get(track) ?? readable(track);

  return (context) => (
    <InterviewTracksPanel
      surface={surface}
      state={state}
      nameOfTrack={nameOfTrack}
      projectId={projectId}
      context={context}
      catalog={catalog}
    />
  );
}
